import React, { useMemo } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import LoadingButton from "@mui/lab/LoadingButton";
import { useFormik } from "formik";
import { UsersThree, X } from "@phosphor-icons/react";
import { ANDatePicker, ANLoadingScreen } from "@answersnow/pkg-theme";
import { format, subDays } from "date-fns";
import { formatToPhoneNumber, getOnlyChangedValues } from "utils/formatFormFields";
import { dateWithoutTimezone } from "utils/dateWithoutTimezone";
import { COUNTRIES, STATES, TIMEZONES, LANGUAGES } from "@constants";
import { memberValidationSchema } from "../Profile/Member/validationSchema";
import { validateEmail } from "utils/validation";
import { useGetUserById } from "hooks/api/user/useGetUserById";
import { TeamMember, TeamRole } from "./interface";
import GuardianRequiredDialog from "../../Widgets/TeamSection/GuardianRequiredDialog";
import { RequiresDesignatedCaregiverDialog } from "./RequiresDesignatedCaregiverDialog";

export interface EditTeamMemberFormValues {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  relationshipType: number | string;
  isDesignatedCaregiver: boolean;
  isLegalGuardian: boolean;
  dateOfBirth: Date | null;
  gender: string;
  preferredLanguage: string;
  address1: string;
  address2: string;
  country: string;
  state: string;
  city: string;
  zipCode: string;
  timezone: string;
}

interface EditTeamMemberDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (changedValues: Partial<EditTeamMemberFormValues>) => Promise<void>;
  member: TeamMember | null;
  teamRoles: TeamRole[];
  allTeamMembers: TeamMember[];
  isSubmitting?: boolean;
  onSendInvite?: () => void;
  userPermissions?: any;
}

type MemberStatus = "active" | "invite_pending" | "record_only";

function getMemberStatus(member: TeamMember | null): MemberStatus {
  if (!member || member.hasAcceptedInvite) return "active";
  const isExpired =
    !member.expirationDate || new Date(member.expirationDate) < new Date();
  return isExpired ? "record_only" : "invite_pending";
}

function getInviteSentDate(member: TeamMember | null): string | null {
  if (!member?.expirationDate) return null;
  const expiration = new Date(member.expirationDate);
  const sentDate = subDays(expiration, 7);
  return format(sentDate, "MMM d, yyyy");
}

/**
 * Dialog for editing an existing team member's profile.
 * Shows status-aware alerts for Invite Pending and Record Only members.
 */
const EditTeamMemberDialog: React.FC<EditTeamMemberDialogProps> = ({
  open,
  onClose,
  onSubmit,
  member,
  teamRoles,
  allTeamMembers,
  isSubmitting = false,
  onSendInvite,
  userPermissions,
}) => {
  const { user, userLoading } = useGetUserById(open && member?.userId ? member.userId : null);

  const [legalGuardianDialogOpen, setLegalGuardianDialogOpen] = React.useState(false);
  const [designatedCaregiverDialogOpen, setDesignatedCaregiverDialogOpen] = React.useState(false);

  const status = getMemberStatus(member);
  const canEditTeamFields = member?.hasAcceptedInvite ?? true;

  const legalGuardiansCount = useMemo(() => {
    return allTeamMembers.filter((m) => m.isLegalGuardian === true).length;
  }, [allTeamMembers]);

  const designatedCaregiversCount = useMemo(() => {
    return allTeamMembers.filter((m) => m.isDesignatedCaregiver === true).length;
  }, [allTeamMembers]);

  const initialValues: EditTeamMemberFormValues = useMemo(() => {
    const merged = { ...member, ...user };
    return {
      relationshipType: member?.relationshipType ?? "",
      isDesignatedCaregiver: member?.isDesignatedCaregiver ?? false,
      isLegalGuardian: member?.isLegalGuardian ?? false,
      firstName: merged?.firstName ?? "",
      lastName: merged?.lastName ?? "",
      dateOfBirth: merged?.dateOfBirth
        ? new Date(dateWithoutTimezone(merged.dateOfBirth))
        : null,
      gender: merged?.gender ?? "",
      preferredLanguage: merged?.preferredLanguage ?? "",
      phoneNumber: merged?.phoneNumber ?? "",
      email: merged?.email ?? "",
      address1: merged?.address1 ?? "",
      address2: merged?.address2 ?? "",
      country: merged?.country ?? "",
      state: merged?.state ?? "",
      city: merged?.city ?? "",
      zipCode: String(merged?.zipCode ?? ""),
      timezone: merged?.timezone ?? "",
    };
  }, [member, user]);

  const formik = useFormik<EditTeamMemberFormValues>({
    enableReinitialize: true,
    initialValues,
    validationSchema: memberValidationSchema,
    onSubmit: async (values, { resetForm }) => {
      const changedValues = getOnlyChangedValues(initialValues as any, values as any);
      try {
        await onSubmit(changedValues);
        resetForm();
      } catch {
        // keep form edits intact — errors shown as toasts by parent
      }
    },
  });

  const handleClose = () => {
    if (!isSubmitting) {
      formik.resetForm();
      onClose();
    }
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const phone = e.target.value.replace(/[^\d]/g, "");
    formik.handleChange({ target: { name: "phoneNumber", value: phone } });
  };

  const handleEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { value } = e.target;
    formik.setFieldTouched("email", true);
    if (!validateEmail(value)) {
      formik.setErrors({ ...formik.errors, email: "Invalid email address" });
    } else {
      formik.setFieldError("email", undefined);
    }
    formik.handleBlur(e);
  };

  const handleLegalGuardianChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.checked && legalGuardiansCount <= 1) {
      setLegalGuardianDialogOpen(true);
      return;
    }
    formik.handleChange(e);
  };

  const handleDesignatedCaregiverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.checked && designatedCaregiversCount <= 1) {
      setDesignatedCaregiverDialogOpen(true);
      return;
    }
    formik.handleChange(e);
  };

  const memberName = member?.name || `${initialValues.firstName} ${initialValues.lastName}`.trim();

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        aria-labelledby="edit-team-member-dialog-title"
      >
        <DialogTitle
          id="edit-team-member-dialog-title"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Stack direction="row" alignItems="center" gap={3}>
            <UsersThree weight="duotone" size={24} />
            <Typography variant="h4" component="span">
              {memberName}
            </Typography>
          </Stack>
          <IconButton onClick={handleClose} aria-label="Close dialog" size="small">
            <X size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {userLoading ? (
            <ANLoadingScreen />
          ) : (
            <form id="edit-team-member-form" onSubmit={formik.handleSubmit}>
              <Grid container rowSpacing={5}>
                {status === "invite_pending" && (
                  <Grid xs={12}>
                    <Alert severity="warning">
                      Invitation sent on {getInviteSentDate(member)}.
                    </Alert>
                  </Grid>
                )}

                {status === "record_only" && (
                  <Grid xs={12}>
                    <Alert
                      severity="info"
                      action={
                        onSendInvite && (
                          <Button
                            variant="text"
                            color="info"
                            size="small"
                            onClick={onSendInvite}
                            sx={{ whiteSpace: "nowrap", fontWeight: 500 }}
                          >
                            Send Account Invite
                          </Button>
                        )
                      }
                    >
                      This team member doesn&apos;t have an AnswersNow account.
                    </Alert>
                  </Grid>
                )}

                <Grid xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="isDesignatedCaregiver"
                        checked={formik.values.isDesignatedCaregiver}
                        onChange={handleDesignatedCaregiverChange}
                        disabled={!canEditTeamFields}
                      />
                    }
                    label="Designated Caregiver"
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", pl: 8 }}
                  >
                    Caregivers get all the updates and messages while the clients are in
                    care.
                  </Typography>
                </Grid>

                <Grid xs={12} sx={{ pt: "0 !important" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="isLegalGuardian"
                        checked={formik.values.isLegalGuardian}
                        onChange={handleLegalGuardianChange}
                        disabled={!canEditTeamFields}
                      />
                    }
                    label="Legal Guardian"
                  />
                </Grid>

                <Grid xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Relationship to Client"
                    name="relationshipType"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.relationshipType}
                    disabled={!canEditTeamFields}
                  >
                    <MenuItem disabled value="">
                      Select
                    </MenuItem>
                    {teamRoles.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid xs={12}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="firstName"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.firstName}
                    error={formik.touched.firstName && !!formik.errors.firstName}
                    helperText={formik.touched.firstName && formik.errors.firstName}
                  />
                </Grid>

                <Grid xs={12}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="lastName"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.lastName}
                    error={formik.touched.lastName && !!formik.errors.lastName}
                    helperText={formik.touched.lastName && formik.errors.lastName}
                  />
                </Grid>

                <Grid xs={12}>
                  <ANDatePicker
                    label="Date of Birth"
                    format="MM/dd/yyyy"
                    disableFuture
                    name="dateOfBirth"
                    value={formik.values.dateOfBirth}
                    onChange={(value: Date | null) => {
                      formik.handleChange({ target: { name: "dateOfBirth", value } });
                    }}
                    onBlur={formik.handleBlur}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        placeholder: "MM/DD/YYYY",
                        error:
                          formik.touched.dateOfBirth && !!formik.errors.dateOfBirth,
                        helperText:
                          formik.touched.dateOfBirth && formik.errors.dateOfBirth,
                      },
                      popper: {
                        disablePortal: false,
                        sx: { zIndex: 1400 },
                      },
                    }}
                  />
                </Grid>

                <Grid xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Gender"
                    name="gender"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.gender}
                  >
                    <MenuItem disabled value="">
                      Select
                    </MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Non-binary">Non-binary</MenuItem>
                    <MenuItem value="Gender Fluid">Gender Fluid</MenuItem>
                    <MenuItem value="Unknown">-</MenuItem>
                  </TextField>
                </Grid>

                <Grid xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Preferred Language"
                    name="preferredLanguage"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.preferredLanguage}
                    disabled={!canEditTeamFields}
                  >
                    <MenuItem disabled value="">
                      Select
                    </MenuItem>
                    {LANGUAGES.map((lang: string) => (
                      <MenuItem key={lang} value={lang}>
                        {lang}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid xs={12}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phoneNumber"
                    onChange={handlePhoneNumberChange}
                    onBlur={formik.handleBlur}
                    value={formatToPhoneNumber(formik.values.phoneNumber)}
                    error={formik.touched.phoneNumber && !!formik.errors.phoneNumber}
                    helperText={
                      formik.touched.phoneNumber && formik.errors.phoneNumber
                    }
                  />
                </Grid>

                <Grid xs={12}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    name="email"
                    onChange={formik.handleChange}
                    onBlur={handleEmailBlur}
                    value={formik.values.email}
                    error={formik.touched.email && !!formik.errors.email}
                    helperText={formik.touched.email && formik.errors.email}
                    disabled={
                      userPermissions &&
                      !userPermissions.update_client_email_address
                    }
                  />
                </Grid>

                <Grid xs={12}>
                  <TextField
                    fullWidth
                    label="Address 1"
                    name="address1"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.address1}
                  />
                </Grid>

                <Grid xs={12}>
                  <TextField
                    fullWidth
                    label="Address 2"
                    name="address2"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.address2}
                  />
                </Grid>

                <Grid xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Country"
                    name="country"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.country}
                  >
                    <MenuItem disabled value="">
                      Select
                    </MenuItem>
                    {Object.entries(COUNTRIES).map(([key, value]) => (
                      <MenuItem key={key} value={key}>
                        {value as string}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="State"
                    name="state"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.state}
                    disabled={formik.values.country !== "US"}
                  >
                    <MenuItem disabled value="">
                      Select
                    </MenuItem>
                    {Object.entries(STATES).map(([key, value]) => (
                      <MenuItem key={key} value={key}>
                        {value as string}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid xs={12}>
                  <TextField
                    fullWidth
                    label="City"
                    name="city"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.city}
                  />
                </Grid>

                <Grid xs={12}>
                  <TextField
                    fullWidth
                    label="Zip Code"
                    name="zipCode"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.zipCode}
                  />
                </Grid>

                <Grid xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Timezone"
                    name="timezone"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.timezone}
                  >
                    <MenuItem disabled value="">
                      Select
                    </MenuItem>
                    {Object.entries(TIMEZONES).map(([key, value]) => (
                      <MenuItem key={key} value={key}>
                        {value as string}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </form>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 6, pb: 5 }}>
          <Button onClick={handleClose} color="secondary" disabled={isSubmitting}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            form="edit-team-member-form"
            variant="contained"
            loading={isSubmitting}
            disabled={!formik.isValid || !formik.dirty}
          >
            Save Changes
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <GuardianRequiredDialog
        open={legalGuardianDialogOpen}
        onClose={() => setLegalGuardianDialogOpen(false)}
      />

      <RequiresDesignatedCaregiverDialog
        open={designatedCaregiverDialogOpen}
        onClose={() => setDesignatedCaregiverDialogOpen(false)}
      />
    </>
  );
};

export default EditTeamMemberDialog;
